// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Button, Group, Loader, Pagination, Paper, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { type JSX, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { TableAnchor } from '../../../components/layout/TableAnchor';
import type { PatientEncounterNote } from '../../../utils/patient-export';

const PAGE_SIZE = 5;

export interface EncounterNotesSectionProps {
  patientId: string;
  notes: PatientEncounterNote[];
  loading: boolean;
}

function formatDate(value: string | undefined): string {
  if (!value) {
    return '—';
  }

  const formatted = dayjs(value);
  return formatted.isValid() ? formatted.format('MM/DD/YYYY') : '—';
}

export function EncounterNotesSection({ patientId, notes, loading }: EncounterNotesSectionProps): JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return notes;
    }

    return notes.filter((note) =>
      `${note.title} ${note.assessment} ${note.noteToPatient}`.toLowerCase().includes(normalizedQuery)
    );
  }, [notes, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const showing = filtered.length === 0 ? 0 : Math.min(safePage * PAGE_SIZE, filtered.length);

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={2}>Encounter Notes</Title>
        <Group gap="sm">
          <TextInput
            placeholder="Search…"
            value={query}
            onChange={(event) => {
              setQuery(event.currentTarget.value);
              setPage(1);
            }}
            leftSection={<IconSearch size={14} />}
            size="sm"
            w={200}
            aria-label="Search encounter notes"
          />
        </Group>
      </Group>

      {loading ? (
        <Stack align="center" py="md">
          <Loader size="sm" />
        </Stack>
      ) : (
        <>
          <Box style={{ overflowX: 'auto' }}>
            <Table highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Assessment</Table.Th>
                  <Table.Th>Note to Patient</Table.Th>
                  <Table.Th>Date Added</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {paginated.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text ta="center" c="dimmed" py="md">
                        {notes.length === 0 ? 'No encounter notes yet.' : 'No encounter notes match your search.'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  paginated.map((note) => (
                    <Table.Tr key={note.encounterId}>
                      <Table.Td>
                        <TableAnchor
                          size="sm"
                          onClick={() => navigate(`/Patient/${patientId}/Encounter/${note.encounterId}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          {note.title}
                        </TableAnchor>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={1}>
                          {note.assessment || '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={1}>
                          {note.noteToPatient || '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{formatDate(note.dateAdded)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          variant="subtle"
                          onClick={() => navigate(`/Patient/${patientId}/Encounter/${note.encounterId}`)}
                        >
                          Edit
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Box>

          {filtered.length > PAGE_SIZE && (
            <Group justify="space-between" mt="md">
              <Text size="sm" c="dimmed">
                {showing} of {filtered.length}
              </Text>
              <Pagination total={totalPages} value={safePage} onChange={setPage} />
            </Group>
          )}
        </>
      )}
    </Paper>
  );
}
